import { AfterViewInit, Component, inject, Input as RouterInput, ViewChild } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { MatButtonModule } from '@angular/material/button';
import { IamRolesComponent } from '../../iam/iam-roles.component';
import { BaseComponent } from '../../shared/base.component';
import { UpdateEmployeeState } from '../../shared/state-interface';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { EmployeeComponent } from '../shared/employee.component';
import { Employee } from '../shared/employee.model';
import { EmployeeService } from '../shared/employee.service';

@Component({
    selector: 'app-update-employee',
    standalone: true,
    imports: [
        MatButtonModule,
        SiteHeaderComponent,
        EmployeeComponent,
        IamRolesComponent
    ],
    templateUrl: './update-employee.component.html',
    styleUrl: './update-employee.component.css'
})
export class UpdateEmployeeComponent extends BaseComponent implements AfterViewInit {

    @RouterInput() id: number = -1;
    @ViewChild(EmployeeComponent) employeeComponent!:EmployeeComponent;
    @ViewChild(IamRolesComponent) iamRolesComponent!:IamRolesComponent;

    private employeeService: EmployeeService = inject(EmployeeService);

    private static _retrievingEmployee = "Retrieving Employee";
    private static _retrievingEmployeeError = `${UpdateEmployeeComponent._retrievingEmployee} error:`;
    private static _updatingEmployee = "Updating Employee";
    private static _updatingEmployeeError = `${UpdateEmployeeComponent._updatingEmployee} error:`;

    private _employee: Employee = new Employee();

    public ngAfterViewInit() {
        const state: UpdateEmployeeState | undefined = this.stateManager.updateEmployeeState;

        if (state !== undefined) {
            this.employeeComponent.restoreState(state.employeeComponentState);
            this.iamRolesComponent.restoreState(state.iamRolesComponentState);
        } else {
            this.loadEmployee(this.id);
            this.employeeComponent.init();
            this.iamRolesComponent.init("E");
        }
    }

    public ngOnDestroy() {
        const state: UpdateEmployeeState = {} as UpdateEmployeeState;
        state.employeeComponentState = this.employeeComponent.getState();
        state.iamRolesComponentState = this.iamRolesComponent.getState();
        state.baseComponentState = super.getState();
        this.stateManager.updateEmployeeState = state;
    }


    public async loadEmployee(id: number)  {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.employeeService.getEmployee(await getIdToken(currentUser), id, true);
            super.message = "";
            super.showSnackBar(`${UpdateEmployeeComponent._retrievingEmployee}...`, -1);

            observable.subscribe(response => {
                // For testing to validate proper rendering of form fields
                // when retrieving data over slow internet connections.
                //setTimeout(() => {
                    try {
                        const json = JSON.parse(JSON.stringify(response));
                        if (json["get-employee"] == "success") {
                            // Convert generic JSON object to type of Employee.
                            this._employee = Employee.toEmployee(json["employee"]);
                            this.employeeComponent.populateFormFromModel(this._employee);
                            this.iamRolesComponent.userRoles = this._employee.user_roles;
                            super.message = "";
                        } else {
                            throw new Error(json["message"]);
                        }
                    } catch (error) {
                        const message = `${UpdateEmployeeComponent._retrievingEmployeeError} ${(<Error>error).message}`;
                        this.message = message;
                    }
                    super.dismissSnackBar();
                //}, 5000);
            })
        } else {
            const message = `${UpdateEmployeeComponent._retrievingEmployeeError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    public cancel() {
        this.router.navigate(["/view-employees"], { state: { restore: true } });
    }

    public async save() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            this._employee = this.employeeComponent.createModelFromForm(this._employee);
            this._employee.user_roles = this.iamRolesComponent.assignedRoles;

            const observable = await this.employeeService.updateEmployee(await getIdToken(currentUser), this._employee);
            this.message = "";
            super.showSnackBar(`${UpdateEmployeeComponent._updatingEmployee}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["update-employee"] == "success") {
                        const queryParam = `Employee '${this._employee.first_name} ${this._employee.last_name}' updated.`;
                        this.router.navigate(["/view-employees"], { queryParams: {"overrideSnackBar": queryParam}, state: { refresh: true } });
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${UpdateEmployeeComponent._updatingEmployeeError} ${(<Error>error).message}`;
                    this.message = message;
                    super.dismissSnackBar();
                }
            })
        } else {
            const message = `${UpdateEmployeeComponent._updatingEmployeeError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }
}